import { FunctionComponent, memo } from 'react'

import styled, { css } from 'styled-components'

import {
  desktopCSS,
  mobileAndTabletCSS,
  mobileCSS,
  tabletCSS,
} from '@measures/responsive'
import { printCSS } from '@utils/style'
import useEnableEnhancedSearchPage from '@hooks/useEnableEnhancedSearchPage'
import useFooter from '@hooks/useFooter'
import useGetPageIdentity from '@hooks/useGetPageIdentity'
import useIsErrorPage from '@hooks/useIsErrorPage'

import FooterComponent from './FooterComponent'
import { GetFooterItemsType } from './types'
import { getFooterItems } from './utils'

interface FooterWrapperProps {
  isError: boolean
  isEnhancedSearchPage: boolean
}

const FooterWrapper = styled.footer<FooterWrapperProps>`
  ${({
    isError,
    isEnhancedSearchPage,
    theme: {
      colors: { fillDefault },
      spacing: { spacing16, spacing24, spacing40, spacing56 },
    },
  }) => css`
    ${printCSS(css`
      display: none;
    `)}
    ${isError
      ? css`
          ${desktopCSS(css`
            display: grid;
            grid-template-columns: 994px;
            justify-content: center;
          `)};

          ${mobileAndTabletCSS(css`
            display: block;
          `)};
        `
      : css`
          background-color: ${fillDefault};
          display: grid;
          grid-row-gap: ${spacing24};

          ${desktopCSS(css`
            ${isEnhancedSearchPage
              ? css`
                  padding: ${spacing56} 15px 138px 15px; //138px = 56px + 82px (height of sticky input field on the enhanced search page)
                `
              : css`
                  padding: ${spacing56} 15px;
                `}
          `)};
          grid-row-gap: ${spacing40};

          ${tabletCSS(css`
            ${isEnhancedSearchPage
              ? css`
                  padding: ${spacing24} 68px 106px 68px;
                `
              : css`
                  padding: ${spacing24} 68px;
                `}
          `)};

          ${mobileCSS(css`
            ${isEnhancedSearchPage
              ? css`
                  padding: ${spacing24} ${spacing16} 106px ${spacing16};
                `
              : css`
                  padding: ${spacing24} ${spacing16};
                `}
          `)};
        `}
  `}
`

const Footer: FunctionComponent<{ className?: string }> = ({ className }) => {
  const { blick: footerBlick, ringier: footerRingier } = useFooter()
  const isErrorPage = useIsErrorPage()
  const { isSearchPage } = useGetPageIdentity()
  const isEnhancedSearchPage = useEnableEnhancedSearchPage() && isSearchPage

  const {
    columns: columnsForMobileBlick,
    rows: rowsForMobileBlick,
    items: dataForMobileBlick,
  } = footerBlick
    ? getFooterItems(footerBlick.links, 2)
    : ({} as ReturnType<GetFooterItemsType>)

  const {
    columns: columnsForNonMobileBlick,
    rows: rowsForNonMobileBlick,
    items: dataForNonMobileBlick,
  } = footerBlick
    ? getFooterItems(footerBlick.links, 3)
    : ({} as ReturnType<GetFooterItemsType>)

  const {
    columns: columnsForMobileRingier,
    rows: rowsForMobileRingier,
    items: dataForMobileRingier,
  } = footerRingier
    ? getFooterItems(footerRingier.links, 2)
    : ({} as ReturnType<GetFooterItemsType>)

  const {
    columns: columnsForNonMobileRingier,
    rows: rowsForNonMobileRingier,
    items: dataForNonMobileRingier,
  } = footerRingier
    ? getFooterItems(footerRingier.links, 3)
    : ({} as ReturnType<GetFooterItemsType>)

  return (
    <FooterWrapper
      isError={!!isErrorPage}
      className={className}
      isEnhancedSearchPage={isEnhancedSearchPage}
    >
      {!!footerBlick && (
        <FooterComponent
          title={footerBlick.text}
          logoType="blick"
          blinkProps={footerBlick.link}
          columnsForMobile={columnsForMobileBlick}
          rowsForMobile={rowsForMobileBlick}
          dataForMobile={dataForMobileBlick}
          columnsForNonMobile={columnsForNonMobileBlick}
          rowsForNonMobile={rowsForNonMobileBlick}
          dataForNonMobile={dataForNonMobileBlick}
        />
      )}
      {!!footerRingier && (
        <FooterComponent
          isExpandable={true}
          initialExpandedState="collapsed"
          title={footerRingier.text}
          logoType="ringier"
          blinkProps={footerRingier.link}
          columnsForMobile={columnsForMobileRingier}
          rowsForMobile={rowsForMobileRingier}
          dataForMobile={dataForMobileRingier}
          columnsForNonMobile={columnsForNonMobileRingier}
          rowsForNonMobile={rowsForNonMobileRingier}
          dataForNonMobile={dataForNonMobileRingier}
        />
      )}
    </FooterWrapper>
  )
}

export { FooterWrapper }

const MemoizedFooter = memo(Footer, () => true)

MemoizedFooter.displayName = 'MemoizedFooter'

export default MemoizedFooter
